import { DeliveryOrderResponse, ShippingDetails } from "../delivery-order/delivery-order.types";
import { PaymentMethod } from "../payment/payment.types";

export interface OrderResponse {
    id: string;
    orderNumber: string;
    userDetails: UserDetails;
    orderItems: OrderItem[];
    orderStatus: OrderStatus;
    createdAt: string;
    completedAt: string;
    paymentDetails: PaymentDetails;
    shippingDetails: ShippingDetails;
    reference: string;
    specialInstructions: string;
    orderSource: OrderSource;
    transactionNumber: string;
    failedReason: string;
    failedBy: string;
    orderType: OrderType;
    sourceBranchCode: string;
    deliveryMethod: DeliveryMethod;
    deliveryOrder?: DeliveryOrderResponse;
}

export interface UserDetails {
    phoneNumber: string;
    email?: string;
    membershipId?: string;
    name?: string;
    identificationIdNo?: string;
}

export interface PaymentDetails {
    paymentMethod: string;
    paymentStatus: string;
    totalAmount: number;
    paymentDate: string;
    discountDetails: DiscountDetails;
    taxAmount: number;
    paymentReference: string;
}

export interface OrderItem {
    transferItemId?: string;
    requestedSku?: string;
    requestedName?: string;
    productSku: string;
    productName: string;
    productType?: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
    simBatchId: number;
    simId: number;
    notes: string;
    /** Allocated SIM ICCIDs after fulfillment (order-service OrderItem.iccids) */
    iccids?: string[];
    componentItems?: ComponentItem[];
}

export interface ComponentItem {
    productSku: string;
    productName: string;
    productType: string;
    quantity: number;
}

export interface Pagination {
    length: number;
    size: number;
    page: number;
    lastPage: number;
    startIndex: number;
    endIndex: number;
}

export interface OrderRequest {
    userDetails: UserDetails;
    orderItems: OrderItemRequest[];
    shippingDetails: ShippingDetails;
    paymentMethod: PaymentMethod;
    reference: string;
    specialInstructions: string;
    orderSource: OrderSource;
    orderType: OrderType;
    sourceBranchCode: string;
    deliveryMethod: DeliveryMethod;
}

export interface OrderItemRequest {
    productSku: string;
    quantity: number;
    notes: string;
    /** For KIT products: maps each component productGroup id to the chosen variant SKU */
    selectedComponentSkus?: Record<string, string>;
}

export interface OrderPaidRequest {
    paymentReference: string;
}

export interface OrderCompletionRequest {
    completedBy: string;
    completionNotes: string;
}

export interface SimActivationRetryRequest {
    msisdn: string;
    iccid: string;
    activationRefId?: string | null;
    success: boolean;
}

export interface OrderCancelRequest {
    cancellationReason: string;
}

export interface DiscountDetails {
    discountAmount: number;
    originalAmount: number;
    discountedAmount: number;
    productDiscounts: ProductDiscount[];
    rankDiscounts: RankDiscount[];
    orderDiscounts: OrderDiscount[];
}

export interface ProductDiscount {
    discountCode: string;
    discountValueType: string;
    discountValue: number;
    productSku: string;
    originalAmount: number;
    discountAmount: number;
}

export interface RankDiscount {
    discountCode: string;
    discountValueType: string;
    discountValue: number;
    rankName: string;
    orderAmount: number;
    discountAmount: number;
}

export interface OrderDiscount {
    discountCode: string;
    discountValueType: string;
    discountValue: number;
    orderAmount: number;
    discountAmount: number;
}

// -----------
// Enum
// -----------

export enum OrderStatus {
    PENDING = 'PENDING',
    PROCESSING = 'PROCESSING',
    PO_PENDING_APPROVAL = 'PO_PENDING_APPROVAL',
    PO_APPROVED = 'PO_APPROVED',
    PO_REJECTED = 'PO_REJECTED',
    PAYMENT_PENDING = 'PAYMENT_PENDING',
    PAYMENT_COMPLETED = 'PAYMENT_COMPLETED',
    PAYMENT_FAILED = 'PAYMENT_FAILED',
    ITEMS_RECEIVING = 'ITEMS_RECEIVING',
    ITEMS_PARTIALLY_RECEIVED = 'ITEMS_PARTIALLY_RECEIVED',
    ITEMS_RECEIVED = 'ITEMS_RECEIVED',
    ITEMS_INSPECTED = 'ITEMS_INSPECTED',
    DO_CREATED = 'DO_CREATED',
    DO_PROCESSING = 'DO_PROCESSING',
    DO_SHIPPED = 'DO_SHIPPED',
    DO_DELIVERED = 'DO_DELIVERED',
    COMPLETED = 'COMPLETED',
    CANCELLED = 'CANCELLED',
    FAILED = 'FAILED'
}

export enum OrderSource {
    WALK_IN = 'WALK_IN',
    MOBILE_APP = 'MOBILE_APP',
    SUPPLIER_DIRECT = 'SUPPLIER_DIRECT',
    STOCK_TRANSFER = 'STOCK_TRANSFER',
    TNG = "TNG"
}

export enum OrderType {
    CUSTOMER_PURCHASE = 'CUSTOMER_PURCHASE', // For regular customer orders (mobile or walk-in)
    SUPPLIER_RECEIPT = 'SUPPLIER_RECEIPT', // For direct inventory receipts from suppliers
    BRANCH_TRANSFER = 'BRANCH_TRANSFER', // For inter-branch stock transfers
}

export enum UnitOfMeasurement {
    PCS = 'PCS',    // Pieces
    KG = 'KG',     // Kilograms
    G = 'G',      // Grams
    L = 'L',      // Liters
    ML = 'ML',     // Milliliters
    M = 'M',      // Meters
    CM = 'CM',     // Centimeters
    BOX = 'BOX'     // Boxes
}

export enum POStatus {
    DRAFT = 'DRAFT',
    PENDING_APPROVAL = 'PENDING_APPROVAL',
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED',
    CANCELLED = 'CANCELLED'
}

export enum DOStatus {
    CREATED = 'CREATED', // DO created
    PICKING = 'PICKING', // DO is in picking process
    PACKED = 'PACKED', // DO is packed
    READY_FOR_PICKUP = 'READY_FOR_PICKUP', // DO is ready for pickup
    PICKED_UP = 'PICKED_UP', // DO is picked up
    IN_TRANSIT = 'IN_TRANSIT', // DO is in transit
    DELIVERED = 'DELIVERED', // DO is delivered
    FAILED_DELIVERY = 'FAILED_DELIVERY', // DO failed to deliver
    PARTIALLY_DELIVERED = 'PARTIALLY_DELIVERED', // DO is partially delivered
    CANCELLED = 'CANCELLED' // DO cancelled before fulfillment
}

export enum DeliveryMethod {
    SELF_COLLECT = 'SELF_COLLECT',
    SHIPPING = 'SHIPPING'
}

export interface ExportJobResponse {
    jobId: string;
    status: ExportJobStatus;
    createdAt: string;
    completedAt: string;
    errorMessage: string;
    downloadUrl: string;
}

export enum ExportJobStatus {
    PENDING = 'PENDING',
    PROCESSING = 'PROCESSING',
    READY = 'READY',
    FAILED = 'FAILED'
}

